import type { NavigateFunction } from 'react-router-dom';
import type {
  ThreadProposalHandoff,
  ToolHandoffOrigin,
  ToolLaunchContext,
} from '@shared/types/continuations';
import { isActivitySubjectUuid } from '@shared/types/activity-subject-routes';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const normalizePrefill = (value: unknown): Record<string, string> => {
  if (!isRecord(value)) return {};

  return Object.entries(value).reduce<Record<string, string>>(
    (accumulator, [key, entry]) => {
      if (typeof entry === 'string') {
        accumulator[key] = entry;
      }
      return accumulator;
    },
    {}
  );
};

const normalizeOrigin = (value: unknown): ToolHandoffOrigin | null => {
  if (!isRecord(value)) return null;
  if (
    typeof value.sourceRunId !== 'string' ||
    typeof value.sourceActionId !== 'string' ||
    typeof value.sourceActionIndex !== 'number' ||
    typeof value.sourceActionLabel !== 'string'
  ) {
    return null;
  }

  return {
    sourceRunId: value.sourceRunId,
    sourceActionId: value.sourceActionId,
    sourceActionIndex: value.sourceActionIndex,
    sourceActionLabel: value.sourceActionLabel,
    sourceViewType:
      typeof value.sourceViewType === 'string' ? value.sourceViewType : null,
    subjectId: isActivitySubjectUuid(
      typeof value.subjectId === 'string' ? value.subjectId : null
    )
      ? String(value.subjectId)
      : null,
    accountSubjectId: isActivitySubjectUuid(
      typeof value.accountSubjectId === 'string' ? value.accountSubjectId : null
    )
      ? String(value.accountSubjectId)
      : null,
  };
};

const normalizeProposalHandoff = (value: unknown): ThreadProposalHandoff | null => {
  if (!isRecord(value)) return null;
  const proposalId = typeof value.proposalId === 'string' ? value.proposalId : null;
  const topicId = typeof value.topicId === 'string' ? value.topicId : null;
  const expectedTopicVersion = value.expectedTopicVersion;
  const topicTitle = typeof value.topicTitle === 'string' ? value.topicTitle.trim() : '';
  const subjectId =
    typeof value.subjectId === 'string' && isActivitySubjectUuid(value.subjectId)
      ? value.subjectId
      : null;
  const accountSubjectId =
    typeof value.accountSubjectId === 'string' &&
    isActivitySubjectUuid(value.accountSubjectId)
      ? value.accountSubjectId
      : null;
  if (
    !proposalId ||
    !isActivitySubjectUuid(proposalId) ||
    !topicId ||
    !isActivitySubjectUuid(topicId) ||
    !Number.isInteger(expectedTopicVersion) ||
    Number(expectedTopicVersion) < 1 ||
    !topicTitle
  ) {
    return null;
  }
  return {
    proposalId,
    topicId,
    expectedTopicVersion: Number(expectedTopicVersion),
    topicTitle,
    subjectId,
    accountSubjectId,
  };
};

const normalizeLaunchContext = (value: unknown): ToolLaunchContext | null => {
  if (!isRecord(value)) return null;
  const subjectId =
    typeof value.subjectId === 'string' && isActivitySubjectUuid(value.subjectId)
      ? value.subjectId
      : null;
  const accountSubjectId =
    typeof value.accountSubjectId === 'string' &&
      isActivitySubjectUuid(value.accountSubjectId)
      ? value.accountSubjectId
      : null;
  const topicId =
    typeof value.topicId === 'string' && isActivitySubjectUuid(value.topicId)
      ? value.topicId
      : null;
  const expectedTopicVersion = Number.isInteger(value.expectedTopicVersion) &&
      Number(value.expectedTopicVersion) >= 1
    ? Number(value.expectedTopicVersion)
    : null;
  const decisionId =
    typeof value.decisionId === 'string' && isActivitySubjectUuid(value.decisionId)
      ? value.decisionId
      : null;
  const objectiveRef =
    typeof value.objectiveRef === 'string' && value.objectiveRef.startsWith('objective:')
      ? value.objectiveRef.slice(0, 500)
      : null;
  if (!subjectId) return null;
  if (
    [topicId, expectedTopicVersion, decisionId].some((entry) => entry !== null) &&
    (!topicId || !expectedTopicVersion || !decisionId)
  ) return null;
  if (objectiveRef && !decisionId) return null;
  return {
    subjectId,
    accountSubjectId,
    topicId,
    expectedTopicVersion,
    decisionId,
    objectiveRef,
  };
};

export const navigateToToolWithPrefill = (
  navigate: NavigateFunction,
  params: {
    toolKey: string;
    prefill: Record<string, string>;
    origin?: ToolHandoffOrigin;
    proposalHandoff?: ThreadProposalHandoff;
    launchContext?: ToolLaunchContext;
  }
): void => {
  navigate(`/tools/${encodeURIComponent(params.toolKey)}`, {
    state: {
      toolPrefill: params.prefill,
      toolHandoffOrigin: params.origin ?? null,
      threadProposalHandoff: params.proposalHandoff ?? null,
      toolLaunchContext: params.launchContext ?? null,
    },
  });
};

export const resolveThreadProposalHandoff = (
  locationState: unknown
): ThreadProposalHandoff | null => {
  if (!isRecord(locationState)) return null;
  return normalizeProposalHandoff(locationState.threadProposalHandoff);
};

export const resolveToolPrefill = (
  searchParams: URLSearchParams,
  locationState: unknown
): Record<string, string> => {
  if (isRecord(locationState)) {
    const statePrefill = normalizePrefill(locationState.toolPrefill);
    if (Object.keys(statePrefill).length > 0) return statePrefill;
  }

  const prefill = searchParams.get('prefill');
  if (prefill) {
    try {
      return normalizePrefill(JSON.parse(prefill));
    } catch {
      return {};
    }
  }

  return {};
};

export const resolveToolHandoffOrigin = (
  searchParams: URLSearchParams,
  locationState: unknown
): ToolHandoffOrigin | null => {
  if (isRecord(locationState)) {
    const stateOrigin = normalizeOrigin(locationState.toolHandoffOrigin);
    if (stateOrigin) return stateOrigin;
  }

  const legacyOrigin = searchParams.get('origin');
  if (!legacyOrigin) return null;

  try {
    return normalizeOrigin(JSON.parse(legacyOrigin));
  } catch {
    return null;
  }
};

export const resolveToolLaunchContext = (
  locationState: unknown
): ToolLaunchContext | null => {
  if (!isRecord(locationState)) return null;
  return normalizeLaunchContext(locationState.toolLaunchContext);
};

/** S1: a tool opened from an object asks for its form, even when nothing could be prefilled. */
export const resolveToolLaunchRequested = (locationState: unknown): boolean =>
  isRecord(locationState) && locationState.toolLaunch === true;
